import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  useToast,
  IconButton,
  Badge,
  Divider,
  useColorModeValue,
  UnorderedList,
  ListItem,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { markNotificationAsRead } from '../utils/notificationUtils';
import type { Notification } from '../types/notification';
import { format, formatDistanceToNow } from 'date-fns';

const NotificationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [loading, setLoading] = useState(true);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    const fetchNotification = async () => {
      if (!id) {
        toast({
          title: 'Error',
          description: 'Notification ID is missing',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);
        const notificationDoc = await getDoc(doc(db, 'notifications', id));
        
        if (!notificationDoc.exists()) {
          toast({
            title: 'Not Found',
            description: 'Notification not found',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          navigate('/dashboard');
          return;
        }

        const notificationData = {
          id: notificationDoc.id,
          ...notificationDoc.data(),
          created_at: notificationDoc.data().created_at?.toDate(),
          read_at: notificationDoc.data().read_at?.toDate(),
          expires_at: notificationDoc.data().expires_at?.toDate(),
        } as Notification;

        setNotification(notificationData);

        // Mark as read if not already
        if (!notificationData.isRead) {
          await markNotificationAsRead(id);
          setNotification(prev => prev ? { ...prev, isRead: true, read_at: new Date() } : null);
        }
      } catch (error) {
        console.error('Error fetching notification:', error);
        toast({
          title: 'Error',
          description: 'Failed to load notification',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchNotification();
  }, [id, navigate, toast]);

  const handleBack = () => {
    // Navigate to dashboard or previous location
    // Prefer going back in history (to /notifications when available), fallback to dashboard
    try {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
    } catch (e) {
      // ignore and fallback
    }
    navigate('/dashboard');
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'normal':
        return 'blue';
      case 'low':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getTypeDisplay = (type: string) => {
    switch (type) {
      case 'sales_order_created':
        return 'Sales Order Created';
      case 'sales_order_updated':
        return 'Sales Order Updated';
      case 'sales_order_deleted':
        return 'Sales Order Deleted';
      case 'sales_order_approved':
        return 'Sales Order Approved';
      case 'sales_order_approver':
        return 'Approver Assignment';
      case 'billing_alert':
        return 'Billing Alert';
      case 'vehicle_reminder':
        return 'Vehicle Reminder';
      case 'reminder':
        return 'Reminder';
      case 'system':
        return 'System';
      default:
        return type;
    }
  };

  const computeRepeatText = (repeatInterval?: string, repeatEveryMonths?: any) => {
    const ri = repeatInterval || 'none';
    switch (ri) {
      case 'none':
        return 'none';
      case 'monthly':
        return 'repeat every month';
      case 'quarterly':
        return 'repeat every 3 months';
      case 'semi-annual':
        return 'repeat every 6 months';
      case 'annual':
        return 'repeat every year';
      case 'custom': {
        const months = Number(repeatEveryMonths) || 1;
        return months === 1 ? 'repeat every month' : `repeat every ${months} months`;
      }
      default:
        return String(ri);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={8}>
        <VStack spacing={4} align="center" justify="center" minH="50vh">
          <Spinner size="xl" />
          <Text>Loading notification...</Text>
        </VStack>
      </Container>
    );
  }

  if (!notification) {
    return (
      <Container maxW="container.md" py={8}>
        <VStack spacing={4} align="center" justify="center" minH="50vh">
          <Text fontSize="xl">Notification not found</Text>
          <Button onClick={handleBack}>Go Back</Button>
        </VStack>
      </Container>
    );
  }
  // Get fields from notification data
  const getField = (field: string) => {
    const n = notification as any;
    return n[field] !== undefined && n[field] !== null ? n[field] : n.data?.[field];
  };

  const dueDate = getField('dueDate');
  const alertDays = getField('alertDays');
  const alertDaysConfig = getField('alertDaysConfig');
  const documents = getField('documents') || getField('attachments') || getField('files') || getField('docs') || getField('document') || getField('documentsList');
  const topRepeatIntervalText = getField('repeatIntervalText');
  const topRepeatInterval = getField('repeatInterval');
  const topRepeatEveryMonths = getField('repeatEveryMonths');

  const finalRepeatText = topRepeatIntervalText || computeRepeatText(topRepeatInterval, topRepeatEveryMonths);

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      <Container maxW="container.md" py={4}>
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center">
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="ghost"
              size="lg"
            />
            <Badge colorScheme={getPriorityColor(notification.priority)} fontSize="md" px={3} py={1}>
              {notification.priority.toUpperCase()}
            </Badge>
          </HStack>

          {/* Notification Card */}
          <Box
            bg={bgColor}
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="lg"
            p={6}
            boxShadow="sm"
          >
            <VStack spacing={4} align="stretch">
              {/* First Section: Reminder Info */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Reminder
                </Text>
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="80px">
                      Title:
                    </Text>
                    <Text fontSize="sm" flex="1" textAlign="right">
                      {notification.data?.reminderTitle || notification.title}
                    </Text>
                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="80px">
                      Message:
                    </Text>
                    <Text fontSize="sm" flex="1" textAlign="right">
                      {notification.data?.reminderDescription || notification.message}
                    </Text>
                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="80px">
                      Type:
                    </Text>
                    <Badge variant="outline" fontSize="sm">
                      {getTypeDisplay(notification.type)}
                    </Badge>
                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="80px">
                      Created:
                    </Text>
                    <Text fontSize="sm" flex="1" textAlign="right">
                      {notification.created_at
                        ? `${format(notification.created_at, 'PPpp')} (${formatDistanceToNow(notification.created_at, { addSuffix: true })})`
                        : 'N/A'}
                    </Text>
                  </HStack>

                  {notification.read_at && (
                    <HStack justify="space-between" align="start">
                      <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="80px">
                        Read:
                      </Text>
                      <Text fontSize="sm" flex="1" textAlign="right">
                        {`${format(notification.read_at, 'PPpp')} (${formatDistanceToNow(notification.read_at, { addSuffix: true })})`}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>

              <Divider />

              {/* Second Section: Additional Information */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Additional Information
                </Text>
                <VStack spacing={2} align="stretch">
                  {dueDate && (
                    <HStack justify="space-between" align="start">
                      <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="100px">
                        Due Date:
                      </Text>
                      <Text fontSize="sm" flex="1" textAlign="right">
                        {format(new Date(dueDate), 'MMMM d, yyyy')}
                      </Text>
                    </HStack>
                  )}

                  {alertDays !== undefined && alertDays !== null && (
                    <HStack justify="space-between" align="start">
                      <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="100px">
                        Alert Days:
                      </Text>
                      <Text fontSize="sm" flex="1" textAlign="right">
                        {alertDays} {alertDays === 1 ? 'day' : 'days'} before due
                      </Text>
                    </HStack>
                  )}

                  {finalRepeatText && finalRepeatText !== 'none' && (
                    <HStack justify="space-between" align="start">
                      <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="100px">
                        Repeat:
                      </Text>
                      <Text fontSize="sm" flex="1" textAlign="right">
                        {finalRepeatText}
                      </Text>
                    </HStack>
                  )}

                  {alertDaysConfig && Array.isArray(alertDaysConfig) && alertDaysConfig.length > 0 && (
                    <HStack justify="space-between" align="start">
                      <Text fontSize="sm" fontWeight="bold" color="gray.500" minW="120px">
                        Alerting Days:
                      </Text>
                      <Text fontSize="sm" flex="1" textAlign="right">
                        {alertDaysConfig.join(', ')}
                      </Text>
                    </HStack>
                  )}

                  {Array.isArray(documents) && documents.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.500" mb={2}>
                        Documents:
                      </Text>
                      <UnorderedList pl={6} spacing={2}>
                        {documents.map((docItem: any, idx: number) => {
                          if (typeof docItem === 'string') {
                            const isUrl = /^https?:\/\//i.test(docItem);
                            return isUrl ? (
                              <ListItem key={idx} fontSize="sm">
                                <ChakraLink href={docItem} color="blue.500" isExternal>
                                  {docItem}
                                </ChakraLink>
                              </ListItem>
                            ) : (
                              <ListItem key={idx} fontSize="sm">{docItem}</ListItem>
                            );
                          }

                          if (docItem && typeof docItem === 'object') {
                            const label = docItem.name || docItem.filename || docItem.title || JSON.stringify(docItem);
                            const href = docItem.url || docItem.downloadUrl || docItem.path || undefined;
                            return href ? (
                              <ListItem key={idx} fontSize="sm">
                                <ChakraLink href={href} color="blue.500" isExternal>
                                  {label}
                                </ChakraLink>
                              </ListItem>
                            ) : (
                              <ListItem key={idx} fontSize="sm">{label}</ListItem>
                            );
                          }

                          return <ListItem key={idx} fontSize="sm">{String(docItem)}</ListItem>;
                        })}
                      </UnorderedList>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Close Button */}
          <Button
            colorScheme="blue"
            size="lg"
            w="full"
            onClick={handleClose}
          >
            Close
          </Button>
        </VStack>
      </Container>
    </Box>
  );
};

export default NotificationDetailPage;
